import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useAuth } from '@wehunt/core-web'
import type { GeoLine } from '@wehunt/ui'
import { listTeamMapLines } from './api/teamMapLinesApi'
import { dtoToGeoLine } from './mapLineDtoMappers'

export interface GroupMapLines {
  lines: GeoLine[]
  /**
   * False only while the answer is still unknown. Callers that BOTH hand these
   * lines to an editor and later diff against them must wait for this, because
   * an empty `lines` from an unresolved query is indistinguishable from "this
   * team has no lines" — and a save diffs the editor's set against this one,
   * so mounting the editor early makes every loaded line look deleted (see
   * MapEditorRoute in routes.tsx).
   *
   * A failed query counts as resolved: `lines` is then empty on BOTH sides of
   * that diff, which produces no writes at all, whereas blocking forever would
   * strand the editor behind a spinner.
   */
  isResolved: boolean
}

export function useGroupMapLines(groupId: string): GroupMapLines {
  const { state } = useAuth()
  const isSignedIn = state.status === 'signed-in'
  const enabled = isSignedIn && !!groupId

  const { data, status } = useQuery({
    queryKey: ['clubhouse', 'teams', groupId, 'map-lines'],
    enabled,
    queryFn: async () => {
      const token = await (state as Extract<typeof state, { status: 'signed-in' }>).user.getIdToken()
      return listTeamMapLines(groupId, token)
    },
  })

  // Mapped once per answer rather than per render — see the note in
  // useGroupHuntingGrounds: the route builds the map object out of this, and a
  // new array each render re-frames the team map under whoever is reading it.
  const lines = useMemo(
    () =>
      // Drop a malformed document rather than throwing, mirroring the mobile
      // app's own element-wise skip: one bad line must never blank the whole map.
      (data ?? [])
        .map(dtoToGeoLine)
        .filter((line): line is GeoLine => line !== null),
    [data],
  )

  return {
    lines,
    // A disabled query never leaves 'pending', so treat "we were never going
    // to ask" as resolved rather than as permanently loading.
    isResolved: !enabled || status !== 'pending',
  }
}
